import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { IOption } from '@/components/Dropdown';
import { DropdownMulti } from '@/components/DropdownMulti';
import { ReviewCard } from '@/components/ReviewCard';
import SearchBar from '@/components/SearchBar';
import { Spinner } from '@/components/Spinner';
import useDebounce from '@/hooks/useDebounce';
import { useTitle } from '@/hooks/useTitle';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { requestAllCategories } from '@/store/modules/categories';
import { requestReviews } from '@/store/modules/reviews';
import styles from './styles.module.scss';

const PAGE_SIZE = 8;

export function ReviewsPage() {
  useTitle('DienTEA | Vídeos');
  const dispatch = useAppDispatch();
  const { list, loading, total } = useAppSelector((state) => state.reviews);
  const { all: categories } = useAppSelector((state) => state.categories);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [filter, setFilter] = useState<IOption[]>([]);

  const hasMore = total > list.length;
  const endReached = total === list.length && list.length > 0;

  useEffect(() => {
    dispatch(requestAllCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      requestReviews({
        page: 0,
        pageSize: PAGE_SIZE,
        title: debouncedSearch,
        categoryIds: filter.map((i) => i.key)
      })
    );
  }, [debouncedSearch, dispatch, filter]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    await dispatch(
      requestReviews({
        page: list.length / PAGE_SIZE,
        pageSize: PAGE_SIZE,
        title: debouncedSearch,
        categoryIds: filter.map((i) => i.key)
      })
    );
  }, [list.length, loading, hasMore, dispatch, debouncedSearch, filter]);

  return (
    <div className={styles.reviewsPage}>
      <section className={styles.container}>
        <div className={styles.sectionHeader}>
          <SearchBar value={search} onChange={setSearch} />
          <DropdownMulti
            options={categories ? categories.map((cat) => ({ key: cat.id, label: cat.name })) : []}
            value={filter}
            onChange={setFilter}
            placeholder='Categorías'
          />
        </div>

        {total === 0 && !loading ? <p>No se han encontrado vídeos.</p> : null}

        <div className={styles.reviews}>
          {list.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>

        {!list.length && debouncedSearch ? <p>No se han encontrado resultados para "{debouncedSearch}".</p> : null}

        {hasMore ? (
          <>
            {loading && <Spinner />}

            <div className={styles.btnContainer}>
              <Button onClick={loadMore}>{loading ? 'Cargando...' : 'Cargar más'}</Button>
            </div>
          </>
        ) : endReached ? (
          <div className={styles.endMessage}>
            <p>✨ Has visto todos los videos ✨</p>
          </div>
        ) : null}
      </section>
    </div>
  );
}
